import { useState } from "react";
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Paper, Grid, Autocomplete, TextField, CircularProgress } from "@mui/material";
import Header from "./Header";
import LessorFilter from "./LessorFilter";
import AssetFilter from "./AssetFilter";
import { EndDateFilter, StartDateFilter } from "./DateFilter";
import { getMRFundBalanceReportApi } from "../apiService";
import {  globalPostService } from "../../../utils/globalApiServices";
import { checkApiStatus } from "../../../utils";
import { ExportMenu, PageLoader, titanAviationIntance } from "../../../shared_elements";
import { isValidYearRange } from "../../../utils/helper";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { trackActivity } from "../../../utils/mixpanel";

const MRFundBalanceReport = ({ pltConstants, dropDownLoader }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false)
  const [reportData, setReportData] = useState({});
  const [error, setError] = useState({});
  const [showMore, setShowMore] = useState(false);
  const onFieldChange = (key, value) => {
    setReportData((prev) => {
      const updatedData = { ...prev, [key]: value };
      if (key === "start_date" && !value) { updatedData.end_date = null; }
      return updatedData;
    });
  };  
  const onFocusChange = (keyParam) => { setError(prev => ({ ...prev, [keyParam]: '' })) }
  const onExportReport = (extention,) => {
    let validationInputs = {};
    validationInputs = {
      start_date: !reportData?.start_date ? (titanAviationIntance ? "" : "Please select Start Date") : !isValidYearRange(reportData?.start_date) ? "Year must be between 1900 and 2100." : "",
      end_date: !reportData?.end_date ? "Please select End Date" : (reportData?.start_date && moment(reportData.end_date).isBefore(moment(reportData.start_date))) ? "End date must be after Start Date" : !isValidYearRange(reportData?.end_date) ? "Year must be between 1900 and 2100." : "",
    };
    if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; })) {
      const getIdArray = (arr) => arr?.length ? arr.map((i) => i.id) : undefined;
      const lessorIds = getIdArray(reportData?.lessor_name_id);
      const lesseeIds = getIdArray(reportData?.lessee_ids);
      const payload = {
        file_type: extention,start_date: reportData?.start_date, end_date: reportData?.end_date,
        ...(lessorIds && { lessor_name_id: lessorIds }),
        ...(lesseeIds && { lessee_ids: lesseeIds }),
        ...(reportData?.asset_type_id?.length && { assets: reportData?.asset_type_id, }),
      }
      setLoading(true)
      globalPostService(getMRFundBalanceReportApi(),payload)
        .then(response => {
          setLoading(false)
          if (checkApiStatus(response)) {
            setReportData({})
            enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { vertical: "top", horizontal: "right" }, })
            trackActivity('Item Export', { page_title: `MR Fund Balance Report`, success_msg: response?.data?.message, payload: payload, response: response?.data })
          } else {
            enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" }, })
            trackActivity('Item Export', { page_title: `MR Fund Balance Report`, error_msg: response?.data?.message, payload: payload, response: response?.data })
          }
        });
    } else {
      setError(validationInputs)
      trackActivity('Item Export', { page_title: `MR Fund Balance Report`, error_msg: validationInputs })
    }
  };
  return (
    <Paper square>
      <div className='custom-rpt-card mr-fund-balance-card'>
        <div className='rpt-card-header'>
          <Grid container spacing={1} className='rpt-header-grid'>
            <Grid size={{ md: 10, xs: 8 }}>
              <Header title="MR Fund Balance Report" description="This report contains the MR fund balance grouped by lessor and lessee for each asset." />
            </Grid>
            <Grid size={{ md: 2, xs: 4 }} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
              <ExportMenu
                exportReportFn={(file) => onExportReport(file.extension)}
                files={[{ title: 'EXCEL', extension: 'xlsx' }, { title: 'CSV', extension: 'csv' }]}
              />
            </Grid>
          </Grid>
        </div>
        <Grid sx={{ padding: 2 }} container spacing={1} >
          <Grid size={{ md: 6, xs: 12 }} >
            <StartDateFilter reportData={reportData} onFieldChange={onFieldChange} onFocusChange={onFocusChange} error={error} isFutureDisabled={true} isRequired={!titanAviationIntance} monthYearOnly={titanAviationIntance} />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <EndDateFilter reportData={reportData} onFieldChange={onFieldChange} onFocusChange={onFocusChange} error={error} disabled={titanAviationIntance ? false : !reportData?.start_date} monthYearOnly={titanAviationIntance} />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <LessorFilter pltConstants={pltConstants} dropDownLoader={dropDownLoader} reportData={reportData} onFieldChange={onFieldChange} />
          </Grid>
          <Grid size={{ md: 6, xs: 12 }}>
            <p className='lessor-select'><span onClick={() => setReportData({ ...reportData, lessee_ids: pltConstants?.lessee })}>Select All Lessee</span>{reportData?.lessee_ids?.length ? <span style={{ marginLeft: '8px' }} onClick={() => { setReportData({ ...reportData, lessee_ids: [] }); }} >Remove All Lessee</span> : null}</p>
            <Autocomplete
              disablePortal
              options={pltConstants?.lessee || []}
              multiple={true}
              value={reportData?.lessee_ids || []}
              getOptionLabel={(option) => option.name || ""}
              onChange={(e, newValue) => onFieldChange('lessee_ids', newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lessee"
                  margin='normal'
                  size='small'
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {dropDownLoader?.lessee && (
                            <CircularProgress color="inherit" size={18} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                    inputLabel: { shrink: true },
                  }}
                  placeholder="Select Lessee(s)"
                />
              )}
            />
          </Grid>
          {!showMore && (
          <Grid size={{ md: 12, xs: 12 }}>
            <p className='lessor-select'><span className='show-more-toggle' onClick={() => setShowMore(true)}>Show more <ChevronRightIcon sx={{ fontSize: 15 }} /></span></p>
          </Grid>
          )}
          {showMore && (<>
          <Grid size={{ md: 6, xs: 12 }}>
            <AssetFilter isRequired={false} pltConstants={pltConstants} dropDownLoader={dropDownLoader} reportData={reportData} onFieldChange={onFieldChange} onFocusChange={onFocusChange} />
          </Grid>
          <Grid size={{ md: 12, xs: 12 }}>
            <p className='lessor-select'><span className='show-more-toggle' onClick={() => setShowMore(false)}><ChevronLeftIcon sx={{ fontSize: 15 }} /> Show less</span></p>
          </Grid>
          </>)}
        </Grid>
      </div>
      {isLoading ? <PageLoader /> : null}
    </Paper>
  )
}

export default MRFundBalanceReport